import { z } from "zod/v4";

export const leagueFormatSchema = z.enum(["doubles", "singles"]);
export const leagueGenderRuleSchema = z.enum(["open", "men", "women"]);
export const leagueStatusSchema = z.enum(["active", "archived"]);
export const leagueEntryStatusSchema = z.enum(["active", "withdrawn", "removed"]);
export const leagueSeasonStatusSchema = z.enum(["active", "closed"]);

export const leagueSeasonSchema = z.object({
	ends_on: z.string().nullable(),
	id: z.string().uuid(),
	number: z.number().int(),
	starts_on: z.string(),
	status: leagueSeasonStatusSchema,
});

export const leagueViewerEntrySchema = z.object({
	id: z.string().uuid(),
	status: leagueEntryStatusSchema,
});

// `eligibility` is "eligible" or a declined-join code (same codes a failed
// join returns) — the UI never re-derives it from the rules.
export const leagueViewerSchema = z.object({
	eligibility: z.string(),
	entry: leagueViewerEntrySchema.nullable(),
});

export const leagueSchema = z.object({
	age_min: z.number().int().nullable(),
	age_under: z.number().int().nullable(),
	can_manage: z.boolean(),
	club_id: z.string().uuid(),
	created_at: z.coerce.date(),
	current_season: leagueSeasonSchema.nullable(),
	entry_count: z.number().int(),
	format: leagueFormatSchema,
	gender_rule: leagueGenderRuleSchema,
	id: z.string().uuid(),
	name: z.string(),
	status: leagueStatusSchema,
	viewer: leagueViewerSchema,
});

export const leagueListResponseSchema = z.object({
	items: z.array(leagueSchema),
});

// Rules are immutable after creation — only `name` is ever editable again
// (see RenameBody / archive). age_min/age_under come from the age-preset
// mapping in the club portal, never typed in directly.
export const createLeagueSchema = z.object({
	age_min: z.number().int().positive().optional(),
	age_under: z.number().int().positive().optional(),
	format: leagueFormatSchema,
	gender_rule: leagueGenderRuleSchema,
	name: z.string().min(1).max(80),
	starts_on: z.string().optional(),
});

export const renameLeagueSchema = z.object({
	name: z.string().min(1).max(80),
});

// `rank` is null until the entrant has played enough games to be ranked;
// `games_to_rank` then carries how many are still needed.
export const leagueStandingSchema = z.object({
	avatar_url: z.string().nullable(),
	display_name: z.string(),
	entry_id: z.string().uuid(),
	games: z.number().int(),
	games_to_rank: z.number().int(),
	losses: z.number().int(),
	rank: z.number().int().nullable(),
	rating: z.number().int(),
	rd: z.number().int(),
	user_id: z.string().uuid(),
	wins: z.number().int(),
});

export const leagueEntriesResponseSchema = z.object({
	items: z.array(leagueStandingSchema),
	season: leagueSeasonSchema.nullable(),
});

// Newest first; any active club member can read it.
export const leagueSeasonListResponseSchema = z.object({
	items: z.array(leagueSeasonSchema),
});

// Closes the open season (voiding its unsettled results as `season_closed`)
// and opens season N+1 starting today, or `starts_on` if given (not in the past).
export const startLeagueSeasonSchema = z.object({
	starts_on: z.string().optional(),
});
